import { useRef, useState, type FormEvent } from "react";

export function AccountBillingArrangement({
  account,
  api,
  csrf,
  onSaved,
}: {
  account: {
    id: string;
    kind: string;
    billing_mode?: string;
    version?: string | number;
  };
  api: <T>(
    path: string,
    options?: { method?: string; body?: unknown; csrf?: string | undefined },
  ) => Promise<T>;
  csrf?: string | undefined;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState(account.billing_mode ?? "standard");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const command = useRef({ key: crypto.randomUUID(), fingerprint: "" });
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = {
      billingMode: mode,
      expectedVersion: Number(account.version),
      reason: String(form.get("reason") ?? ""),
      acknowledged: form.get("acknowledged") === "on",
    };
    const fingerprint = JSON.stringify(body);
    if (command.current.fingerprint !== fingerprint)
      command.current = { key: crypto.randomUUID(), fingerprint };
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(`/admin/accounts/${account.id}/billing-arrangement`, {
        method: "POST",
        csrf,
        body: { ...body, requestKey: command.current.key },
      });
      setNotice(
        "Billing arrangement saved. Re-select the service plan to update its quote. No charges were started.",
      );
      await onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="cr-panel cr-editor">
      <summary>Change billing arrangement</summary>
      <p>
        This remains a {account.kind} account. Complimentary service has no
        recurring service charge. Changing this arrangement does not start
        billing, cancel subscriptions, forgive invoices or change calling.
      </p>
      <form className="cr-form" onSubmit={(event) => void submit(event)}>
        <fieldset className="cr-form-fields" disabled={busy}>
          <label className="cr-field">
            <span>Billing arrangement</span>
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value)}
            >
              <option value="standard">Standard pricing</option>
              <option value="complimentary">
                Complimentary — no service charge
              </option>
            </select>
          </label>
          <label className="cr-field">
            <span>Reason and authorization</span>
            <textarea
              name="reason"
              required
              minLength={10}
              maxLength={500}
              rows={3}
            />
          </label>
          <label className="cr-check">
            <input name="acknowledged" type="checkbox" required />{" "}
            <span>
              I am authorized to change this account's billing arrangement.
              Existing invoices and subscriptions are not changed.
            </span>
          </label>
          <button
            className="cr-button"
            disabled={!account.version || mode === account.billing_mode}
          >
            {busy ? "Saving…" : "Save billing arrangement"}
          </button>
        </fieldset>
        {error && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
      </form>
    </details>
  );
}
